import React, { useContext, useEffect, useRef } from 'react';

import { CompanyContext } from 'common/containers/CompanyContainer';
import { OpenModalContext } from 'common/containers/ModalContainer';
import { getEncodedQueryParams } from 'common/containers/QueueItemListContainer';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import cloneElementWithProps from 'common/core/cloneElementWithProps';
import Helmet from 'common/helmets/Helmet';
import AccessModal from 'common/modals/AccessModal';
import { H1, P } from 'common/ui/Text';
import hasPermission from 'common/util/hasPermission';

import AdminQueueAutomationUpsell from './AdminQueueAutomationUpsell';
import AdminQueueSidebar from './AdminQueueSidebar';

import type { Board } from 'common/api/endpoints/boards';
import type {
  Company,
  RolePermissionName,
  ViewerPreferences,
} from 'common/api/endpoints/companies';
import type SyncIntegrationNames from 'common/constants/syncIntegrationNames';

import 'css/components/subdomain/admin/_AdminQueue.scss';

type Counts = {
  totalCount: number;
  cannyCount: number;
  totalSourceCount: number;
  perSourceCounts: Partial<Record<SyncIntegrationNames, number>>;
};

type Props = {
  actionItemCounts: Counts;
  boards: Board[];
  children: React.ReactNode;
  queueItemCounts: Counts;
  inboxTotalItemCount: number;
};

const AdminQueue = (props: Props) => {
  const router = useContext(RouterContext);
  const location = useContext(LocationContext);
  const company = useContext<Company>(CompanyContext);
  const viewer = useContext(ViewerContext);
  const openModal = useContext(OpenModalContext);
  const isAllowed = hasPermission('deleteComments', company, viewer);

  const initialPathname = useRef<string>(location.pathname);
  const initialViewerPreferences = useRef<ViewerPreferences>(company?.viewerPreferences);

  const { queueItemCounts, inboxTotalItemCount, actionItemCounts } = props;

  const counts = {
    spam: inboxTotalItemCount,
    ...(company.queueAutomation?.enabled ? actionItemCounts : queueItemCounts),
  };

  useEffect(() => {
    if (!isAllowed) {
      const permission: RolePermissionName = 'deletePosts';
      router.replace('/admin');
      openModal(
        AccessModal,
        {
          requiredPermissions: [permission],
        },
        {
          allowRouteChange: true,
        }
      );
    }
  }, [router, isAllowed, openModal]);

  // load pre-saved filters. We perform do this here to prevent clashes between Spam and Posts when switching between them
  useEffect(() => {
    const lastUsedFilters = initialViewerPreferences.current?.queueFilters;
    if (!lastUsedFilters) {
      return;
    }

    router.replace({
      pathname: initialPathname.current,
      query: getEncodedQueryParams(lastUsedFilters),
    });
  }, [router]);

  return (
    <div className="adminQueue">
      <Helmet title="Autopilot | Canny" />
      <header className="queueHeader">
        <div className="queueTitle">
          <H1 variant="headingLg">Autopilot</H1>
        </div>
        <P className="queueSubtitle">
          Review incoming feedback from all your sources.{' '}
          <a
            href="https://help.canny.io/en/articles/8202451-beta-inbox"
            target="_blank"
            className="externalLink"
            rel="noopener">
            Learn&nbsp;more.
          </a>
        </P>
      </header>
      <AdminQueueAutomationUpsell company={company} />
      <main className="queueContentLayout">
        <AdminQueueSidebar company={company} counts={counts} />
        {isAllowed && cloneElementWithProps(props.children, props)}
      </main>
    </div>
  );
};

export default AdminQueue;
